import { useState, useEffect, useCallback } from 'react';
import { calendarApi } from '../services/calendarApi';

export const useCalendar = (user) => {
    const [events, setEvents] = useState([]);
    const [todos, setTodos] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchEvents = useCallback(async () => {
        if (!user) return;
        setIsLoading(true);
        try {
            const data = await calendarApi.getEvents();
            setEvents(data);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch events:', err);
            setError('Failed to load events');
        } finally {
            setIsLoading(false);
        }
    }, [user]);

    const fetchTodos = useCallback(async () => {
        if (!user) return;
        setIsLoading(true);
        try {
            const data = await calendarApi.getTodos();
            setTodos(data);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch todos:', err);
            setError('Failed to load todos');
        } finally {
            setIsLoading(false);
        }
    }, [user]);

    const addEvent = async (eventData) => {
        try {
            const newEvent = await calendarApi.createEvent({
                ...eventData
            });
            setEvents(prev => [...prev, newEvent]);
            return newEvent;
        } catch (err) {
            console.error('Failed to add event:', err);
            throw err;
        }
    };

    const updateEvent = async (id, eventData) => {
        try {
            const updated = await calendarApi.updateEvent(id, eventData);
            setEvents(prev => prev.map(e => e.id === id ? updated : e));
            return updated;
        } catch (err) {
            console.error('Failed to update event:', err);
            throw err;
        }
    };

    const deleteEvent = async (id) => {
        try {
            await calendarApi.deleteEvent(id);
            setEvents(prev => prev.filter(e => e.id !== id));
        } catch (err) {
            console.error('Failed to delete event:', err);
            throw err;
        }
    };

    const addTodo = async (todoData) => {
        // Handle both object and string input
        const payload = typeof todoData === 'string'
            ? { title: todoData, completed: false }
            : { ...todoData, completed: false };

        try {
            const newTodo = await calendarApi.createTodo(payload);
            setTodos(prev => [...prev, newTodo]);
            return newTodo;
        } catch (err) {
            console.error('Failed to add todo:', err);
            throw err;
        }
    };

    const updateTodo = async (id, todoData) => {
        try {
            const updated = await calendarApi.updateTodo(id, todoData);
            setTodos(prev => prev.map(t => t.id === id ? updated : t));
            return updated;
        } catch (err) {
            console.error('Failed to update todo:', err);
            throw err;
        }
    };

    const toggleTodo = async (todo) => {
        return updateTodo(todo.id, { ...todo, completed: !todo.completed });
    };

    const deleteTodo = async (id) => {
        try {
            await calendarApi.deleteTodo(id);
            setTodos(prev => prev.filter(t => t.id !== id));
        } catch (err) {
            console.error('Failed to delete todo:', err);
            throw err;
        }
    };

    useEffect(() => {
        if (user) {
            fetchEvents();
            fetchTodos();
        }
    }, [user, fetchEvents, fetchTodos]);

    return {
        events,
        todos,
        isLoading,
        error,
        addEvent,
        updateEvent,
        deleteEvent,
        addTodo,
        updateTodo,
        toggleTodo,
        deleteTodo,
        refresh: () => { fetchEvents(); fetchTodos(); }
    };
};
